// ui/createTrumpDecidedDetectorOrReduced.js: M5 core-game-polish C5.
//
// Detects the snapshot transition into "trump is decided" so the
// renderer can fire the trump splash (Decision A3, CSS-only DOM
// overlay, not <dialog>; pointer-events: none so it never blocks
// dealer_discard or any other mid-game interaction).
//
// Trigger rule: `trump_suit` transitions from a falsy value (null /
// undefined / "") to a real suit code in {S,H,D,C}, OR the
// `maker_seat` changes from falsy to a 1..4 number. We watch both so
// a snapshot that lands the suit + maker together still fires exactly
// once. Resets when both fields go falsy again (new hand) so the
// next trump call re-fires.
//
// Reduced-motion: the sibling factory short-circuits, detect()
// always returns null. Callers fall back to rendering the trump
// chip / badge through the normal snapshot path.

const VALID_SUITS = new Set(['S', 'H', 'D', 'C']);

function isTrumpSet(snap) {
  if (!snap) return false;
  return VALID_SUITS.has(snap.trump_suit);
}

/**
 * @typedef {{
 *   trump_suit: 'S'|'H'|'D'|'C',
 *   maker_seat: 1|2|3|4|null,
 *   caller_went_alone: boolean,
 * }} TrumpDecidedEvent
 */

/**
 * @returns {{ detect: (snap:any) => TrumpDecidedEvent|null, reset: () => void }}
 */
export function createTrumpDecidedDetector() {
  let lastFired = false;

  function detect(snap) {
    const now = isTrumpSet(snap);
    if (!now) {
      // New hand / pre-call snapshot. Re-arm so the next call fires.
      lastFired = false;
      return null;
    }
    if (lastFired) return null;
    lastFired = true;
    return {
      trump_suit: snap.trump_suit,
      maker_seat: snap.maker_seat ?? null,
      caller_went_alone: !!snap.caller_went_alone,
    };
  }

  function reset() { lastFired = false; }

  return { detect, reset };
}

/**
 * Reduced-motion sibling. Same shape, returns null forever so the
 * caller can drop the splash without branching on the media query.
 *
 * @returns {{ detect: (snap:any) => null, reset: () => void }}
 */
export function createTrumpDecidedDetectorOrReduced() {
  return {
    detect: () => null,
    reset: () => {},
  };
}

export const _internals = { isTrumpSet, VALID_SUITS };
